import { tvpil } from '../../../data/compat/tvpil';
import { KalturaAssetDto, KalturaCollectionAsset } from '../../composed';
import { createChannelFilter, createPager } from '../../factories';
import { processCollectionAsset } from '../../processing/collection';
import { fetchAssetList } from '../../services';
import { withCollectionFallback } from '../utils';

import { getDynamicOrderBy, getGroupBy, getOrderBy, getResponseProfile, orderByMapping } from './utils';

type FetchRailCollectionArgs = {
  channelId: number;
  groupBy?: string;
  kSql: string;
  orderBy?: string;
  pageIndex?: number;
  pageSize?: number;
  withRetrievedProperties?: boolean;
};

export const retrievedProperties = [
  'enableCatchUp',
  'endDate',
  'error',
  'id',
  'images',
  'linearAssetId',
  'mediaFiles',
  'metas.Adult',
  'metas.attached_collection_id',
  'metas.ContentMarkerText',
  'metas.Deeplink',
  'metas.DeeplinkURN',
  'metas.EpisodeName',
  'metas.EpisodeNumber',
  'metas.ExternalProvider',
  'metas.ExternalProviderId',
  'metas.in_app_link',
  'metas.Is_Deeplink',
  'metas.MarkerBackgroundColor',
  'metas.MarkerTextColor',
  'metas.SeasonNumber',
  'metas.SeriesID',
  'name',
  'startDate',
  'tags.BillingId',
  'tags.display_type',
  'tags.ParentalRating',
  'tags.type_of_app_link',
  'tags.SeriesName',
  'type',
].join(',');

export function fetchRailCollection<T = KalturaAssetDto>({
  channelId,
  groupBy,
  kSql,
  orderBy,
  pageIndex = 1,
  pageSize = 20,
  withRetrievedProperties = false, // TODO: remove when all frontends will use useGridCollection
}: FetchRailCollectionArgs) {
  // @ts-expect-error TODO Fix
  const assetOrderBy = orderByMapping[orderBy];

  const assetGroupBy = getGroupBy(groupBy);
  const dynamicOrderBy = getDynamicOrderBy(orderBy);
  const responseProfile = getResponseProfile({
    availableRatings: tvpil.availableRatingsNames,
    availableProviders: tvpil.availableProviders,
    groupBy,
  });

  // TODO: remove KalturaAssetDto later
  return fetchAssetList<T>({
    filter: createChannelFilter({
      idEqual: channelId,
      groupBy: assetGroupBy,
      kSql,
      orderBy: assetOrderBy,
      dynamicOrderBy,
    }),
    pager: createPager({
      pageIndex,
      pageSize,
    }),
    responseProfile,
  });
}

type FetchCollectionArgs = {
  collectionId: number;
  groupBy?: string;
  orderBy?: string;
  adultCategoryReferenceId?: string;
  isFilterByEntitlementRequired?: boolean;
  kSql?: string;
  pageIndex?: number;
  pageSize?: number;
};

function getCollectionKsql({
  isFilterByEntitlementRequired,
  adultCategoryReferenceId,
  kSql,
}: {
  isFilterByEntitlementRequired?: boolean;
  adultCategoryReferenceId?: string;
  kSql?: string;
}) {
  const resKsql = [
    adultCategoryReferenceId && `in_app_link!='${adultCategoryReferenceId}'`,
    isFilterByEntitlementRequired && `entitled_assets='entitledSubscriptions'`,
  ]
    .filter(Boolean)
    .join(' ');

  if (kSql) {
    return tvpil.addKsql(kSql, resKsql);
  }

  return resKsql;
}

export async function fetchCollection({
  collectionId,
  groupBy,
  orderBy,
  adultCategoryReferenceId,
  isFilterByEntitlementRequired,
  kSql,
  pageIndex = 1,
  pageSize = 20,
}: FetchCollectionArgs) {
  const assetOrderBy = getOrderBy(orderBy);
  const assetGroupBy = getGroupBy(groupBy);
  const dynamicOrderBy = getDynamicOrderBy(orderBy);
  const responseProfile = getResponseProfile({
    availableRatings: tvpil.availableRatingsNames,
    availableProviders: tvpil.availableProviders,
    retrievedProperties,
    groupBy,
  });
  const resSql = getCollectionKsql({
    isFilterByEntitlementRequired,
    adultCategoryReferenceId,
    kSql,
  });

  const { objects, totalCount } = await withCollectionFallback(() =>
    fetchAssetList<KalturaCollectionAsset>({
      filter: createChannelFilter({
        idEqual: collectionId,
        groupBy: assetGroupBy,
        kSql: resSql,
        orderBy: assetOrderBy,
        dynamicOrderBy,
      }),
      pager: createPager({
        pageIndex,
        pageSize,
      }),
      responseProfile,
    }),
  );

  return {
    objects: objects.map(processCollectionAsset),
    totalCount,
  };
}
